import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, Subscription, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

/** Body of a successful `GET api/session/heartbeat` (ERP-15430). */
interface SessionHeartbeatResponse {
  /** Seconds until the shared cookie's sliding-window expiration. */
  secondsRemaining: number;
}

/** Callbacks for the 30-second heartbeat poll. */
export interface SessionPollHandlers {
  /** Fired once on the first 401 — the shared session has ended. */
  onUnauthorized: () => void;
  /**
   * Fired on every successful (2xx) poll with the shared cookie's remaining
   * lifetime (seconds), or `null` when the value couldn't be read. Lets the
   * caller drive an inactivity warning off the shared-session clock.
   */
  onTick?: (secondsRemaining: number | null) => void;
}

/**
 * V2's polling client for V4's cross-app session-activity endpoints
 * (ERP-15430). V4 OWNS the `linq.session.active` cookie; V2 is a
 * read/write consumer — it bootstraps the cookie if it doesn't exist
 * (V2-first sign-in), refreshes it on debounced user activity, polls
 * every 30 seconds to detect logout-from-V4 or 90-minute idle, and
 * clears it on explicit V2 logout.
 *
 * Calls go to V2's own `/api/session/*` endpoints (same-origin) which
 * proxy server-to-server to V4 (`SessionController` in `Alio.Web`).
 * The same-origin design sidesteps cross-origin CORS preflights from
 * the V2 browser to V4's API Gateway. The `linq.session.active`
 * cookie is scoped to `.erplinq.com` so V4's Set-Cookie response
 * is still visible to V2 once the proxy forwards it back.
 *
 * Bearer is attached by `linqHttpInterceptor`. The V2 proxy forwards
 * it to V4 unchanged; V4 remains the JWT authority.
 *
 * Activated only when the `v2-v4-session-logout` LaunchDarkly flag is
 * enabled — coordinated by `AppComponent`.
 */
@Injectable({ providedIn: 'root' })
export class SessionActivityService {
  private readonly http = inject(HttpClient);
  private readonly ngZone = inject(NgZone);

  private pollSubscription: Subscription | undefined;
  private isPolling = false;

  /**
   * Same-origin path for V2's session proxy. The V2 backend
   * (`SessionController`) is responsible for forwarding to V4 and
   * relaying the status code + any `Set-Cookie` headers back to
   * the browser.
   *
   * Returned WITHOUT a leading slash so the request resolves against
   * `<base href="./" />` and inherits V2's application-path prefix
   * (e.g. `/20_ABCD/`). A leading slash would bypass the prefix and
   * 404 against the root.
   */
  private endpoint(path: string): string {
    return `api/session/${path}`;
  }

  /**
   * Heartbeat the cross-app cookie. Two call patterns:
   *
   *   * `postHeartbeat({ bootstrap: true })` — explicit "I just logged
   *     in, establish the cookie" call. The V4 backend creates the
   *     cookie unconditionally. Sent ONCE per authenticated boot from
   *     `AppComponent.startSessionActivitySync`.
   *
   *   * `postHeartbeat()` — default activity-driven refresh. The V4
   *     backend refreshes the cookie ONLY IF one is already present.
   *     If the cookie was just cleared by V4's `DELETE /session/end`,
   *     this call is a server-side no-op (returns 204 but doesn't
   *     write the cookie back). That's what prevents an activity ping
   *     here from resurrecting a session V4 intentionally ended.
   *
   * Fire-and-forget: errors (network, 5xx) are swallowed so transient
   * blips don't surface to the caller.
   */
  postHeartbeat(opts: { bootstrap?: boolean } = {}): Observable<unknown> {
    const path = opts.bootstrap ? `${this.endpoint('heartbeat')}?bootstrap=true` : this.endpoint('heartbeat');
    return this.http.post(path, null).pipe(catchError(() => EMPTY));
  }

  /**
   * Explicitly clear the cookie. Used by V2's `AuthService.logout()`
   * just before the Auth0 redirect so V4's next 30-second poll picks
   * up the logout within ~30 seconds.
   */
  endSession(): Observable<unknown> {
    return this.http.delete(this.endpoint('end')).pipe(catchError(() => EMPTY));
  }

  /**
   * One-shot liveness check of the shared `linq.session.active` cookie.
   *
   * Resolves `true` when `GET api/session/heartbeat` returns 2xx (cookie
   * present + valid → the peer app is still active), `false` only on a
   * definitive 401 (cookie missing/expired/cleared). Transient/network
   * errors resolve `true` (fail-open) so a blip never forces a spurious
   * logout — the 30-second poll remains the authority for genuine 401s.
   * Mirrors V4's `SessionActivityService.checkSessionAlive`. ERP-15430.
   */
  checkSessionAlive(): Observable<boolean> {
    return this.http.get(this.endpoint('heartbeat'), { observe: 'response' }).pipe(
      map(() => true),
      catchError((err) => of(err?.status === 401 ? false : true)),
    );
  }

  /**
   * Begin polling `GET api/session/heartbeat` every `intervalMs`
   * (default 30 seconds).
   *
   * On each successful (2xx) tick, `handlers.onTick` receives the shared
   * cookie's remaining lifetime in seconds (or `null` if it couldn't be
   * read) — this drives V2's inactivity warning off the SHARED session
   * clock. `handlers.onUnauthorized` fires on the first 401 — the cross-app
   * signal that either the cookie expired (90-min idle in BOTH apps) or V4
   * explicitly logged out — and is expected to drive `AuthService.logout()`.
   * After the 401 the poll terminates. Non-401 errors (network blips, 5xx)
   * are silently swallowed and the next 30-second tick retries.
   *
   * The timer runs outside Angular's zone so it doesn't trigger change
   * detection on every tick, but the callbacks are invoked back INSIDE the
   * zone since they typically open/close Material dialogs.
   */
  startPolling(handlers: SessionPollHandlers, intervalMs = 30_000): void {
    if (this.isPolling) return;
    const url = this.endpoint('heartbeat');
    this.isPolling = true;

    this.ngZone.runOutsideAngular(() => {
      this.pollSubscription = timer(intervalMs, intervalMs)
        .pipe(
          switchMap(() =>
            this.http.get<SessionHeartbeatResponse>(url, { observe: 'response' }).pipe(
              catchError((err) => {
                if (err?.status === 401) {
                  throw err;
                }
                return EMPTY;
              }),
            ),
          ),
        )
        .subscribe({
          next: (res) => {
            const raw = res.body?.secondsRemaining;
            const secondsRemaining = typeof raw === 'number' ? raw : null;
            this.ngZone.run(() => handlers.onTick?.(secondsRemaining));
          },
          error: (err) => {
            this.isPolling = false;
            this.pollSubscription = undefined;
            if (err?.status === 401) {
              this.ngZone.run(() => handlers.onUnauthorized());
            }
          },
        });
    });
  }

  /** Tear down the 30-second poll. Safe to call when not polling. */
  stopPolling(): void {
    if (!this.isPolling) return;
    this.isPolling = false;
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = undefined;
  }
}
